import { useState, useEffect } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { getAdminToken, setAdminToken, clearAdminToken, setAdminEmail, clearAdminEmail } from "../lib/adminApi";
import { toast } from "sonner";
import { Lock, LayoutDashboard, Settings, Briefcase, GraduationCap, Heart, FolderKanban, Users2, BookOpen, Share2, UserCog, LogOut, Loader2, BarChart3, HelpCircle, MessageSquareQuote, HardDrive } from "lucide-react";

interface AdminLoginFormProps {
  email: string;
  setEmail: (e: string) => void;
  password: string;
  setPassword: (p: string) => void;
  loggingIn: boolean;
  onSubmit: (e: React.FormEvent) => void;
}

function AdminLoginForm({ email, setEmail, password, setPassword, loggingIn, onSubmit }: AdminLoginFormProps) {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <form onSubmit={onSubmit} className="w-full max-w-md p-8 border border-border/60 bg-card/65 backdrop-blur-md rounded-2xl shadow-xl space-y-6 animate-fade-in">
        <div className="text-center space-y-2">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-primary/5 dark:bg-brand-primary/10 border border-brand-primary/15 text-brand-primary">
            <Lock className="h-6 w-6" />
          </div>
          <h3 className="text-2xl font-bold text-foreground">Admin Console</h3>
          <p className="text-xs text-muted-foreground">Authenticate to manage portfolio configurations</p>
        </div>

        <div className="space-y-4">
          <div className="space-y-1.5 text-left">
            <Label htmlFor="email">Security Email</Label>
            <Input id="email" type="email" placeholder="example@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="border-border/60" required />
          </div>

          <div className="space-y-1.5 text-left">
            <Label htmlFor="password">Security Password</Label>
            <Input id="password" type="password" placeholder="******" value={password} onChange={(e) => setPassword(e.target.value)} className="border-border/60" required />
          </div>
        </div>

        <Button type="submit" disabled={loggingIn} className="w-full bg-brand-primary text-white hover:bg-brand-primary/95 font-semibold h-11">
          {loggingIn ? (
            <><Loader2 className="h-4.5 w-4.5 mr-2 animate-spin" /> Verifying...</>
          ) : (
            "Sign In"
          )}
        </Button>
      </form>
    </div>
  );
}

interface AdminSidebarProps {
  onLogout: () => void;
}

function AdminSidebar({ onLogout }: AdminSidebarProps) {
  const navItems = [
    { path: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
    { path: "/analytics", label: "Analytics", icon: BarChart3, end: false },
    { path: "/home", label: "Home Settings", icon: Settings, end: false },
    { path: "/professional", label: "Professional", icon: Briefcase, end: false },
    { path: "/academic", label: "Academic", icon: GraduationCap, end: false },
    { path: "/hobby", label: "Hobbies", icon: Heart, end: false },
    { path: "/projects", label: "Projects", icon: FolderKanban, end: false },
    { path: "/community", label: "Community", icon: Users2, end: false },
    { path: "/blog", label: "Blog", icon: BookOpen, end: false },
    { path: "/contact", label: "Contact Socials", icon: Share2, end: false },
    { path: "/faq", label: "FAQ Settings", icon: HelpCircle, end: false },
    { path: "/testimonials", label: "Testimonials", icon: MessageSquareQuote, end: false },
    { path: "/media", label: "Media Library", icon: HardDrive, end: false },
    { path: "/profile", label: "Profile Settings", icon: UserCog, end: false }
  ] as const;

  return (
    <aside className="w-full md:w-64 border border-border/60 bg-card/65 rounded-2xl p-4 space-y-2 shadow-sm shrink-0">
      <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-3 mb-2">Navigation</h4>
      {navItems.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            className={({ isActive }) =>
              `w-full flex items-center gap-3 px-3 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 ${isActive
                ? "bg-brand-primary text-white shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/30"
              }`
            }
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
      <div className="border-t border-border/60 pt-2 mt-4">
        <button onClick={onLogout} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-destructive hover:bg-destructive/5 rounded-xl transition-all">
          <LogOut className="h-4 w-4 shrink-0" />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}

export function AdminPage() {
  const [token, setToken] = useState<string | null>(getAdminToken());
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [loadingContent, setLoadingContent] = useState(true);
  const [content, setContent] = useState<any>(null);

  const fetchContent = async () => {
    setLoadingContent(true);
    try {
      const token = getAdminToken();
      const res = await fetch("/api/content", {
        headers: token ? { "Authorization": `Bearer ${token}` } : {}
      });
      const data = (await res.json()) as any;
      if (data.success) {
        setContent(data);
      } else {
        toast.error("Failed to load portfolio content from database");
      }
    } catch {
      toast.error("Failed to connect to API server");
    } finally {
      setLoadingContent(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchContent();
    }
  }, [token]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setLoggingIn(true);
    try {
      const response = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });
      const data = (await response.json()) as any;
      if (response.ok && data.success) {
        setAdminToken(data.token);
        setAdminEmail(email);
        setToken(data.token);
        toast.success("Successfully logged in!");
      } else {
        toast.error(data.error || "Incorrect email or password");
      }
    } catch {
      toast.error("Failed to authenticate with server");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    clearAdminToken();
    clearAdminEmail();
    setToken(null);
    setContent(null);
    toast.info("Logged out successfully");
  };

  if (!token) {
    return (
      <AdminLoginForm
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        loggingIn={loggingIn}
        onSubmit={handleLogin}
      />
    );
  }

  if (loadingContent || !content) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
        <p className="text-sm font-semibold text-muted-foreground">Loading admin configurations...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row gap-8 py-6 items-start text-left">
      <AdminSidebar onLogout={handleLogout} />
      <main className="flex-1 w-full p-6 border border-border/60 bg-card/65 rounded-2xl shadow-sm min-h-[50vh]">
        <Outlet context={{ content, fetchContent }} />
      </main>
    </div>
  );
}

export default AdminPage;
